import {
  ErrorOption,
  FieldArray,
  FieldArrayPath,
  FieldError,
  FieldErrors,
  FieldValues,
  FormState,
  RegisterOptions,
  SubmitErrorHandler,
  SubmitHandler,
  UseFormRegisterReturn,
  UseFormReturn,
  useFieldArray,
} from "react-hook-form";

import NumberEditor from "../../Global/Editors/NumberEditor";
import CheckboxEditor from "../../Global/Editors/CheckboxEditor";
import TextEditor from "../../Global/Editors/TextEditor";

import SelectEditor from "../../Global/Editors/SelectEditor";

import { IModuleRound, useRound } from "../../../modules/roundHandler";
import { getValueByString, nameof } from "../../Global/Utils";
import { VeckoSchemaPerioder, WeekDayName, IPeriod } from "../../../types/module-type";
import TimePickerEditor from "../../Global/Editors/TimePickerEditor";
import { BaseSyntheticEvent, useState } from "react";
import { Button, Card, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAdd, faClock, faCog, faHome, faRemove, faSlash } from "@fortawesome/free-solid-svg-icons";
import { Colors } from "../../../modules/Colors";
import { MapTooltip } from "../../../types/globalData";
import React from "react";
import { sumPeriods, sumSchedule } from "../../../modules/TimePeriods";
import { IModuleVentilationRoundTidskanaler } from "../../../types/ModuleDatas";
import TimePickerEditor24 from "../../Global/Editors/TimePickerEditor24";

interface IProps {
  data: any;
  formHook: UseFormReturn;
  tidsKanaler: ITidskanalSpec[];

  showSwitch?: boolean;
}

const dayNameTranslation = {
  monday: "Måndag",
  tuesday: "Tisdag",
  wednesday: "Onsdag",
  thursday: "Torsdag",
  friday: "Fredag",
  saturday: "Lördag",
  sunday: "Söndag",
};

export interface IButtonCopySpec {
  caption: string;
  callback: (dayName: WeekDayName) => void;
}

export interface ITidskanalSpec {
  channelPath: string;
  fieldName: string;
  caption: string;
  hasType: boolean;
  disabled?: boolean;
  buttons?: IButtonCopySpec[];
}

const TidsKanalEditor = ({ data, formHook, tidsKanaler, showSwitch }: IProps) => {
  const [selectedDay, setSelectedDay] = useState<WeekDayName>(WeekDayName.monday);
  let showBody = true;
  const harAngettNyaTidskanaler = formHook.watch("data.harAngettNyaTidskanaler");
  if (showSwitch) {
    showBody = harAngettNyaTidskanaler === "Ja";
  }
  let allValues = formHook.getValues();

  const errors = formHook.formState.errors;
  const isTidskanalerInValid: any = getValueByString(errors, "check_Tidskanaler");

  const addPeriod = (ts: ITidskanalSpec) => {
    const values = formHook.getValues();
    const tidsKanaler = getValueByString<any>(values, ts.channelPath);
    if (!tidsKanaler) return;
    const kanal = tidsKanaler[ts.fieldName];
    if (!kanal) return;
    const periods = kanal[selectedDay] as IPeriod[] | undefined;
    if (!periods) return;
    let start = "08:00";
    let end = "18:00";
    if (periods.length) {
      const lastPeriod = periods[periods.length - 1];
      start = lastPeriod.end;
      if (start < "18:00") end = "18:00";
      else end = "23:59";
    }
    periods.push({ start, end, typ: "Helfart" });
    formHook.reset(values);
  };

  const removePeriod = (ts: ITidskanalSpec, index: number) => {
    const values = formHook.getValues();
    const tidsKanaler = getValueByString<any>(values, ts.channelPath);
    if (!tidsKanaler) return;
    const kanal = tidsKanaler[ts.fieldName];
    if (!kanal) return;
    const periods = kanal[selectedDay] as IPeriod[] | undefined;
    if (!periods) return;
    const removed = periods.splice(index, 1);
    formHook.setValue(ts.channelPath, tidsKanaler);
  };

  // const kanaler = watch(fieldNamePrefix);
  const periodTypes = ["Helfart", "Delfart"];

  const copyTidsKanal = (ts: ITidskanalSpec) => {
    const values = formHook.getValues();
    const tidsKanaler = getValueByString<any>(values, ts.channelPath);
    if (!tidsKanaler) return;
    const kanal = tidsKanaler[ts.fieldName];
    if (!kanal) return;
    const periods = kanal[selectedDay] as IPeriod[] | undefined;

    formHook.reset(values);
  };

  //kanal.monday : IPeriod[];

  const periodsSegment = (kanal: VeckoSchemaPerioder, d: ITidskanalSpec) => {
    return (
      <>
        {Object.keys(WeekDayName).map((wd) => {
          const periods = kanal[wd as WeekDayName];
          if (!periods) return null;

          return (
            <tbody key={wd} style={{ display: wd === selectedDay ? "table-row-group" : "none" }}>
              {/* Header row (only visible if wd === selectedDay, but since tbody itself is hidden otherwise, we can always render it) */}
              <tr>
                <td style={{ width: 120 }}>Från</td>
                <td style={{ width: 120 }}>Till</td>
                <td style={{ width: 120 }}>{d.hasType ? "Typ" : ""}</td>
                <td>{d.disabled || periods.length === 0 ? "" : "Ta bort"}</td>
              </tr>

              {periods.map((period, index) => (
                <tr key={index}>
                  <td>
                    <TimePickerEditor24
                      label=""
                      isDisabled={d.disabled}
                      fieldName={`${d.channelPath}.${d.fieldName}.${wd}.${index}.start`}
                      formHook={formHook}
                      options={{
                        validate: {
                          validateStartAfterOrSameAsPreviousEnd: (val: string) => {
                            if (index > 0) {
                              const prev = periods[index - 1];
                              if (prev.end > val) {
                                return `Start måste vara samma eller senare än föregående periods sluttid(${dayNameTranslation[wd as WeekDayName]})`;
                              }
                            }
                          },
                        },
                      }}
                    />
                  </td>
                  <td>
                    <TimePickerEditor24
                      label=""
                      isDisabled={d.disabled}
                      fieldName={`${d.channelPath}.${d.fieldName}.${wd}.${index}.end`}
                      formHook={formHook}
                      options={{
                        validate: {
                          validateStartAfterOrSameAsPreviousEnd: (val: string) => {
                            if (periods[index].start >= val) {
                              return `Slut måste vara senare än periodens starttid(${dayNameTranslation[wd as WeekDayName]})`;
                            }
                          },
                        },
                      }}
                    />
                  </td>
                  {d.hasType ? (
                    <td>
                      <SelectEditor
                        isDisabled={d.disabled}
                        showWhenIsDisabled={true}
                        formHook={formHook}
                        label=""
                        fieldName={`${d.channelPath}.${d.fieldName}.${wd}.${index}.typ`}
                        items={["Helfart", "Delfart"]}
                      />
                    </td>
                  ) : (
                    <td />
                  )}
                  <td>
                    {d.disabled ? null : (
                      <Button variant="danger" onClick={() => removePeriod(d, index)}>
                        <FontAwesomeIcon icon={faRemove} />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          );
        })}
      </>
    );
  };

  //tidskanaler.ventilation.monday : IPeriod[];
  const kanalSegment = (d: ITidskanalSpec, index: number) => {
    const kanal = getValueByString<VeckoSchemaPerioder | undefined>(allValues, d.channelPath + "." + d.fieldName);
    if (!kanal) return <></>;

    return (
      <React.Fragment key={index.toString()}>
        <tr>
          <td colSpan={7} style={{ backgroundColor: Colors.GrayLight }}>
            <span>
              <span style={{ fontSize: 18, width: 300, fontWeight: "bold", display: "inline-block" }}>{d.caption}</span>
              {d.disabled ? null : (
                <>
                  <Button onClick={() => addPeriod(d)}>
                    <FontAwesomeIcon icon={faAdd} /> Lägg till period
                  </Button>
                  {d.buttons ? (
                    <div style={{ display: "inline-block", marginLeft: 10 }}>
                      <button className="btn btn-info dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                        Mer
                      </button>
                      <ul className="dropdown-menu">
                        {d.buttons.map((b) => (
                          <li key={b.caption} role="button" className="dropdown-item" onClick={() => b.callback(selectedDay)}>
                            {b.caption}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </>
              )}
            </span>
          </td>
        </tr>

        <tr key={index.toString() + ".2"}>
          <td colSpan={7}>
            {/* ⬇️ IMPORTANT: no <tbody> wrapper here */}
            <table>{periodsSegment(kanal, d)}</table>
          </td>
        </tr>
      </React.Fragment>
    );
  };

  const dbg = tidsKanaler;

  const wdInfo: any = {};

  for (let ti = 0; ti < tidsKanaler.length; ti++) {
    const spec = tidsKanaler[ti];
    const schedule = getValueByString<any>(allValues, spec.channelPath + "." + spec.fieldName);
    if (schedule) {
      Object.keys(WeekDayName).forEach((wd) => {
        let periods = schedule[wd as WeekDayName];

        if (periods) {
          const dayTot = sumPeriods(periods);
          if (dayTot > 0) wdInfo[wd] = true; // wdInfo[wd] ? wdInfo[wd] + dayTot : dayTot;
        }
      });
    }
  }

  return (
    <>
      <Card className="mb-3">
        <Card.Header>
          <span>
            TIDSKANALER
            {isTidskanalerInValid && <span className="text-danger ms-4 fs-6"> {isTidskanalerInValid.message}</span>}
          </span>
          {showBody && (
            <input
              type="hidden"
              value={new Date().toISOString()}
              {...formHook.register("check_Tidskanaler", {
                validate: {
                  tst: (v) => {
                    const allValues = formHook.getValues();
                    const tidskanaler = allValues.tidsKanaler || (allValues.data?.tidsKanaler as IModuleVentilationRoundTidskanaler);
                    const krävNärvaro = allValues.tidsKanaler ? true : false;
                    if (krävNärvaro) {
                      if (!tidskanaler?.närvaro) return "Närvaro måste anges";
                      const totNärvaro = sumSchedule(tidskanaler.närvaro, undefined);
                      if (!totNärvaro) return "Närvaro måste anges";
                    }

                    if (!tidskanaler?.ventilation) return "Ventilation måste anges";
                    const totVentialation = sumSchedule(tidskanaler.ventilation, undefined);
                    if (!totVentialation) return "Ventilation måste anges";

                    return undefined;
                  },
                },
              })}
              className={"form-control "}
            />
          )}
          {showSwitch && (
            <div className="headerGroup">
              <span style={{ whiteSpace: "nowrap", fontSize: "1rem", fontWeight: 400, lineHeight: 2 }}> Ange nya inställningar</span>
              <SelectEditor label="" formHook={formHook} fieldName="data.harAngettNyaTidskanaler" tooltipId={nameof<MapTooltip>("BFLJ3_harAngettNyaTidskanaler")} items={["Nej", "Ja"]}></SelectEditor>
            </div>
          )}
        </Card.Header>
        {showBody && (
          <Table bordered>
            <thead>
              <tr>
                {Object.keys(WeekDayName).map((wd) => (
                  <th key={wd} role="button" style={{ background: wd === selectedDay ? Colors.BlueLight : undefined }} onClick={() => setSelectedDay(wd as WeekDayName)}>
                    {/* <FontAwesomeIcon icon={faClock} style={{ color: wdInfo[wd] ? Colors.GrayDarkest : Colors.GrayLight }} /> */}
                    <span className="me-sm-2">
                      {wdInfo[wd] ? (
                        <FontAwesomeIcon icon={faClock} style={{ color: Colors.GrayDarkest }} />
                      ) : (
                        <span className="fa-layers fa-fw">
                          <FontAwesomeIcon icon={faClock} color={Colors.GrayLight} />
                          <FontAwesomeIcon icon={faSlash} color={Colors.GrayDark} />
                        </span>
                      )}
                    </span>

                    {dayNameTranslation[wd as WeekDayName]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <>{tidsKanaler.map((d, i) => kanalSegment(d, i))}</>
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );
};

export default TidsKanalEditor;
